import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Header from '../components/Header';
import bgtrace from '../assets/bgtrace1.jpeg';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [inquiryType, setInquiryType] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [submitError, setSubmitError] = useState("");

  // Handle inquiry parameter
  useEffect(() => {
    const inquiry = searchParams.get("inquiry");
    const install = searchParams.get("install") === "true";
    
    if (inquiry === "installation") {
      setInquiryType("installation");
    } else if (inquiry === "returns") {
      setInquiryType("returns");
    } else if (install) {
      setInquiryType("installation");
    }
  }, [searchParams]);

  // Auto-scroll to form when inquiry param is present (desktop only).
  // On mobile, we keep the page at top for better UX.
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth >= 768) {
      const inquiry = searchParams.get("inquiry");
      const install = searchParams.get("install") === "true";

      if (inquiry || install) {
        const el = document.getElementById("contact-form");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Mobile: ensure page is scrolled to top
      window.scrollTo({ top: 0, behavior: "instant" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [searchParams]);

  // ✅ Format input into (###) ###-####
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10); // keep only 10 digits
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (match) {
      const [, area, first, second] = match;
      if (second) {
        return `(${area}) ${first}${second ? "-" + second : ""}`;
      } else if (first) {
        return `(${area}) ${first}`;
      } else if (area) {
        return `(${area}`;
      }
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneValue(formatted);
  };

  const handleNameChange = (e) => {
    const v = e.target.value;
    if (v.length === 0) {
      setNameValue("");
    } else {
      setNameValue(v.charAt(0).toUpperCase() + v.slice(1));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const name = form.querySelector('input[name="contact[name]"]')?.value?.trim() || "";
    const email = form.querySelector('input[name="contact[email]"]')?.value?.trim() || "";
    const phone = form.querySelector('input[name="contact[phone]"]')?.value?.trim() || "";
    const inquiryTypeValue = form.querySelector('select[name="contact[subject]"]')?.value || "Contact Form";
    const vehicle = form.querySelector('input[name="contact[vehicle]"]')?.value?.trim() || "";
    const message = form.querySelector('textarea[name="contact[body]"]')?.value?.trim() || "";

    setSubmitError("");
    setSubmitStatus("sending");

    try {
      const apiUrl = `${window.location.origin}/api/contact/send`;
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          inquiryType: inquiryTypeValue,
          vehicle,
          message,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setSubmitStatus("error");
        setSubmitError(data.error || "Something went wrong. Please try again.");
        return;
      }

      setSubmitStatus("success");
      form.reset();
      setPhoneValue("");
      setNameValue("");
      setInquiryType("");
    } catch (err) {
      setSubmitStatus("error");
      setSubmitError("Failed to send. Please check your connection and try again.");
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      <main 
        className="pt-20 pb-16 px-4 sm:px-6 lg:px-8"
        style={{
          backgroundImage: `url(${bgtrace})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 sm:mb-8 pt-8 sm:pt-10 tracking-tight">
            <span className="text-black">Contact</span>{" "}
            <span className="text-brandred">Us</span>
          </h1>

          <div className="mb-10 bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
            <p className="text-base sm:text-lg lg:text-xl text-gray-800 leading-relaxed mb-4">
              Have a question or want to book an installation service? Fill out the
              form below and we'll get back to you as soon as possible.
            </p>
            <p className="text-base sm:text-lg lg:text-xl text-gray-800 leading-relaxed">
              Prefer to call? Reach us directly at{' '}
              <a 
                href="tel:+16196938907" 
                className="text-brandred font-semibold hover:text-[#E63B3B] underline transition-colors duration-200"
              >
                +1 619-693-8907
              </a>
            </p>
          </div>

        <div id="contact-form">
        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-[#1E1E1E] border border-gray-600 p-6 sm:p-8 rounded-2xl shadow-2xl"
        >

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-200 tracking-wide">
              Name
            </label>
            <input
              type="text"
              name="contact[name]"
              value={nameValue}
              onChange={handleNameChange}
              required
              className="w-full border border-gray-600 px-4 py-3 rounded-lg bg-[#1E1E1E] text-white placeholder-gray-400 focus:ring-2 focus:ring-brandred focus:border-transparent shadow-sm"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-200 tracking-wide">
              Email
            </label>
            <input
              type="email"
              name="contact[email]"
              required
              className="w-full border border-gray-600 px-4 py-3 rounded-lg bg-[#1E1E1E] text-white placeholder-gray-400 focus:ring-2 focus:ring-brandred focus:border-transparent shadow-sm"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-200 tracking-wide">
              Phone
            </label>
            <input
              type="tel"
              name="contact[phone]"
              placeholder="(123) 456-7890"
              value={phoneValue}
              onChange={handlePhoneChange}
              inputMode="numeric"
              maxLength="14" // formatted length
              className="w-full border border-gray-600 px-4 py-3 rounded-lg bg-[#1E1E1E] text-white placeholder-gray-400 focus:ring-2 focus:ring-brandred focus:border-transparent shadow-sm"
            />
          </div>

          {/* Inquiry Type */}
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-200 tracking-wide">
              Inquiry Type
            </label>
            <select
              name="contact[subject]"
              value={inquiryType}
              onChange={(e) => setInquiryType(e.target.value)}
              className="w-full border border-gray-600 px-4 py-3 rounded-lg bg-[#1E1E1E] text-white focus:ring-2 focus:ring-brandred focus:border-transparent shadow-sm"
            >
              <option value="">Select an option</option>
              <option value="General Question">General Question</option>
              <option value="installation">Book an Installation</option>
              <option value="Request a Quote">Request a Quote</option>
              <option value="returns">Returns</option>
            </select>
          </div>

          {/* Vehicle Info */}
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-200 tracking-wide">
              Vehicle Info
            </label>
            <input
              type="text"
              name="contact[vehicle]"
              placeholder="Year / Make / Model"
              className="w-full border border-gray-600 px-4 py-3 rounded-lg bg-[#1E1E1E] text-white placeholder-gray-400 focus:ring-2 focus:ring-brandred focus:border-transparent shadow-sm"
            />
          </div>

          {/* Dynamic Helper Message for Installation Requests */}
          {(inquiryType === "installation" || inquiryType === "Request a Quote") && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 leading-relaxed">
              <h4 className="text-sm font-semibold text-blue-800 mb-3">
                💡 <strong>Help us prepare your quote efficiently:</strong>
              </h4>
              <p className="text-sm text-gray-700 mb-4">
                Please include as many details as possible about your installation request so our team can provide accurate pricing and scheduling:
              </p>
              
              <div className="text-sm text-gray-700 space-y-3">
                <div>
                  <strong className="text-blue-800">🔧 Special instructions and preferences</strong><br />
                  <span className="text-gray-600">(e.g., Rear lights connected to cargo light / Coilovers preloaded to 3" / Switch panel mounted to the left of the steering wheel)</span><br />
                  Include any non-standard requests or setup notes your build requires.
                </div>
                
                <div>
                  <strong className="text-blue-800">🚙 Vehicle year, make, and model</strong><br />
                  List trim level, 2WD or 4x4, and whether it includes KDSS, XREAS, or other factory systems.<br />
                  <em className="text-gray-600">Example: 2021 Toyota Tacoma TRD Off-Road, 4x4.</em>
                </div>
                
                <div>
                  <strong className="text-blue-800">📦 Parts to be installed</strong><br />
                  Provide part numbers or links to the exact parts or kits you're supplying.<br />
                  <em className="text-gray-600">Example: Bilstein 6112 Coilovers (Part #47-310971), SPC UCA (Part #25460).</em>
                </div>
                
                <div>
                  <strong className="text-blue-800">🗓️ Preferred date/time for installation</strong><br />
                  (Available Tue–Sat, 10am–5pm. Requests outside these hours may incur an additional cost.)<br />
                  <em className="text-gray-600">Example: Friday, November 15th, between 10:00 AM – 2:00 PM.</em>
                </div>
                
                <div>
                  <strong className="text-blue-800">📷 Photos</strong><br />
                  You may be asked to send reference photos of your vehicle or parts via email after submitting this form to help our technicians prepare in advance.
                </div>
              </div>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-200 tracking-wide">
              Message
            </label>
            <textarea
              name="contact[body]"
              rows="5"
              required
              className="w-full border border-gray-600 px-4 py-3 rounded-lg bg-[#1E1E1E] text-white placeholder-gray-400 focus:ring-2 focus:ring-brandred focus:border-transparent shadow-sm"
            ></textarea>
          </div>

          {/* Success confirmation */}
          {submitStatus === "success" && (
            <div
              className="rounded-2xl border border-emerald-500/50 bg-gradient-to-br from-emerald-950/90 to-emerald-900/80 p-8 text-center shadow-xl shadow-emerald-900/20"
              role="alert"
            >
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20">
                <CheckCircle2 className="h-10 w-10 text-emerald-400" strokeWidth={2} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Message sent
              </h3>
              <p className="text-emerald-200/90 text-sm leading-relaxed max-w-sm mx-auto">
                Thanks for reaching out. We&apos;ll get back to you soon at the email you provided.
              </p>
            </div>
          )}
          {/* Error message */}
          {submitStatus === "error" && submitError && (
            <div className="rounded-xl border border-red-500/50 bg-red-950/50 text-red-200 px-4 py-3 text-center">
              {submitError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitStatus === "sending"}
            className="w-full bg-gradient-to-r from-brandred to-[#E63B3B] text-white font-bold px-6 py-3 sm:py-4 rounded-lg shadow-lg hover:opacity-90 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {submitStatus === "sending" ? "Sending…" : "Send Message"}
          </button>
        </form>
        </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
